import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { toNumberOrNull } from '@/lib/number';
import { cropPhase, daysBetween, docOn, pondAreaM2, type Crop, type PondFacts } from '@/lib/crop';

import { EmptyNote, Kicker, Section, TabLink } from './shared';
import { useCropHarvests, useCropSamplings } from './data';
import { abwText } from './format';
import { Term } from '@/components/ui/Term';
import { STOCKING_LABELS, fmtDensity, fmtInt, fmtSigned, type StockingFieldKey } from '@/lib/stockingFields';
import { StockingEditDialog } from './StockingEditDialog';
import { useStockingHistory } from './stockingData';

const num = (v: string | number | null | undefined): number | null => toNumberOrNull(v) ?? null;
const date = (d: string) => format(parseISO(d), 'MMM d, yyyy');
const th = 'px-2.5 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500';
const td = 'px-2.5 py-2 tabular-nums';

export function StockingTab({ farmId, pond, crop, canSupervise }: { farmId: string; pond: PondFacts; crop: Crop; canSupervise: boolean }) {
  const area = pondAreaM2(pond.area_ha);
  const pieces = crop.stocking_count ?? null;
  const d = crop.stocking_details;
  const [editing, setEditing] = useState(false);
  // The sub-line explains a figure worked out from the count; a stored override is not that.
  const shownDensity = d?.density_per_m2 ?? crop.stocking_density;
  const densityIsDerived = !!(pieces && area && shownDensity != null && Math.abs(Number(shownDensity) - pieces / area) < 0.01);
  const extras = (d?.stocking_extras ?? {}) as Record<string, string | number>;
  const extraEntries = (['avg_count', 'containers', 'container_type', 'cv_pct'] as StockingFieldKey[]).filter(
    (k) => extras[k] !== undefined && extras[k] !== ''
  );
  const prepDays = crop.prep_start_date && crop.stocking_date ? daysBetween(crop.prep_start_date, crop.stocking_date) : null;

  return (
    <div className="flex flex-col gap-5">
      <Section
        labelledBy="st-details"
        title="Stocking details"
        action={
          canSupervise && crop.stocking_date && cropPhase(crop) !== 'closed' ? (
            <button type="button" onClick={() => setEditing(true)} className="inline-flex min-h-11 items-center px-1 text-sm font-semibold text-primary-700 hover:underline">
              Edit details
            </button>
          ) : undefined
        }
      >
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <Fact
            label="Stocked"
            value={crop.stocking_date ? format(parseISO(crop.stocking_date), 'MMM d') : '—'}
            sub={prepDays !== null ? `DOC 1 · after ${prepDays} prep ${prepDays === 1 ? 'day' : 'days'}` : 'DOC 1'}
          />
          <Fact label="Delivered" value={pieces ? pieces.toLocaleString() : '—'} sub={crop.species} />
          {d?.pieces_ordered != null && (
            <Fact
              label={STOCKING_LABELS.pieces_ordered}
              value={fmtInt(d.pieces_ordered)}
              sub={
                d.ordered_allowance_pct != null
                  ? `+${Number(d.ordered_allowance_pct)}% allowance = ${fmtInt(d.expected_with_allowance)}`
                  : undefined
              }
            />
          )}
          {d?.extra_pcs != null && (
            <Fact
              label="Extra"
              value={`${fmtSigned(d.extra_pcs)} pcs`}
              sub={
                [
                  d.extra_pct_of_ordered != null ? `${Number(d.extra_pct_of_ordered) >= 0 ? '+' : ''}${Number(d.extra_pct_of_ordered)}% of ordered` : null,
                  d.over_allowance_pcs ? `${fmtInt(d.over_allowance_pcs)} over the allowance` : null,
                ]
                  .filter(Boolean)
                  .join(' · ') || undefined
              }
            />
          )}
          <Fact
            label="Density"
            value={fmtDensity(d?.density_per_m2 ?? crop.stocking_density)}
            sub={densityIsDerived ? `pieces ÷ ${Math.round(area!).toLocaleString()} m²` : undefined}
          />
          {d?.pl_stage && <Fact label="PL stage" value={d.pl_stage} />}
          {extraEntries.map((k) => (
            <Fact key={k} label={STOCKING_LABELS[k]} value={String(extras[k])} />
          ))}
          <Fact label="Target harvest" value={crop.target_harvest_date ? format(parseISO(crop.target_harvest_date), 'MMM d') : '—'} />
        </div>
      </Section>
      {canSupervise && crop.stocking_date && <StockingChangeLog farmId={farmId} crop={crop} />}
      {editing && <StockingEditDialog farmId={farmId} pond={pond} crop={crop} onClose={() => setEditing(false)} />}
      <SurvivalTable farmId={farmId} crop={crop} area={area} />
      <HarvestSummary farmId={farmId} pondId={pond.id} crop={crop} />
    </div>
  );
}

function StockingChangeLog({ farmId, crop }: { farmId: string; crop: Crop }) {
  const { data: history = [] } = useStockingHistory(farmId, crop.id, true);
  if (history.length === 0) return null;
  const labels = STOCKING_LABELS as Record<string, string>;
  const labelFor = (k: string) => labels[k] ?? (k === 'stocking_count' ? 'Delivered' : k === 'stocking_density' ? 'Density' : k === 'pl_stage' ? 'PL stage' : k === 'stocking_extras' ? 'Other details' : k);
  const valueText = (v: unknown): string => {
    if (v === null || v === undefined || v === '') return '—';
    if (typeof v === 'object') {
      const parts = Object.entries(v as Record<string, unknown>).map(([k, x]) => `${labelFor(k)} ${String(x)}`);
      return parts.length > 0 ? parts.join(', ') : '—';
    }
    return typeof v === 'number' ? v.toLocaleString() : String(v);
  };
  return (
    <Section labelledBy="st-log" title="Change log">
      <ul className="flex flex-col gap-2 text-sm">
        {history.map((h) => (
          <li key={h.id} className="flex flex-col gap-0.5 border-b border-gray-100 pb-2 last:border-0">
            <span className="text-gray-900">
              {Object.keys(h.new_data).map((k) => `${labelFor(k)}: ${valueText(h.previous_data[k])} → ${valueText(h.new_data[k])}`).join(' · ')}
            </span>
            <span className="text-xs text-gray-500">
              {h.changed_by_name ?? 'Someone'} · {format(parseISO(h.changed_at), 'MMM d, yyyy HH:mm')}
              {h.reason ? ` · ${h.reason}` : ''}
            </span>
          </li>
        ))}
      </ul>
    </Section>
  );
}

function Fact({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <Kicker>{label}</Kicker>
      <span className="text-xl font-bold tabular-nums text-gray-900">{value}</span>
      {sub && <span className="text-xs text-gray-500">{sub}</span>}
    </div>
  );
}

function SurvivalTable({ farmId, crop, area }: { farmId: string; crop: Crop; area: number | null }) {
  const { data: samplings = [] } = useCropSamplings(farmId, crop.id);
  const stocked = crop.stocking_count ?? null;
  const rows = samplings.filter((s) => s.estimated_pieces_alive || s.survival_rate_pct);

  return (
    <Section labelledBy="st-survival" title="Survival" action={<span className="text-xs text-gray-500">Estimates come from Sampling</span>}>
      <div className="relative overflow-x-auto">
        <table className="w-full min-w-[560px] border-collapse text-left text-sm">
          <thead>
            <tr className="border-b border-gray-100">
              <th className={th}>Check</th>
              <th className={th}>Date</th>
              <th className={`${th} text-right`}><Term term="DOC" /></th>
              <th className={`${th} text-right`}>Pieces</th>
              <th className={`${th} text-right`}>Survival</th>
              <th className={`${th} text-right`}>Density</th>
            </tr>
          </thead>
          <tbody>
            {crop.stocking_date && (
              <tr className="border-b border-gray-50">
                <td className="px-2.5 py-2 font-semibold">Stocking</td>
                <td className={td}>{date(crop.stocking_date)}</td>
                <td className={`${td} text-right`}>1</td>
                <td className={`${td} text-right`}>{stocked ? stocked.toLocaleString() : '—'}</td>
                <td className={`${td} text-right`}>{stocked ? '100%' : '—'}</td>
                <td className={`${td} text-right`}>{stocked && area ? (stocked / area).toFixed(1) : '—'}</td>
              </tr>
            )}
            {rows.map((s) => {
              const alive = s.estimated_pieces_alive ?? null;
              const pct = num(s.survival_rate_pct);
              return (
                <tr key={s.id} className="border-b border-gray-50 last:border-0">
                  <td className="px-2.5 py-2 text-gray-700">Sampling estimate</td>
                  <td className={td}>{date(s.sampling_date)}</td>
                  <td className={`${td} text-right`}>{docOn(crop, s.sampling_date) ?? '—'}</td>
                  <td className={`${td} text-right`}>{alive !== null ? alive.toLocaleString() : '—'}</td>
                  <td className={`${td} text-right`}>{pct !== null ? `${pct.toFixed(1)}%` : '—'}</td>
                  <td className={`${td} text-right`}>{alive !== null && area ? (alive / area).toFixed(1) : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {rows.length === 0 && <EmptyNote>No sampling estimates yet.</EmptyNote>}
    </Section>
  );
}

function HarvestSummary({ farmId, pondId, crop }: { farmId: string; pondId: string; crop: Crop }) {
  const { data: harvests = [] } = useCropHarvests(farmId, crop.id);
  const closed = cropPhase(crop) === 'closed';
  const survival = num(crop.survival_pct);

  return (
    <Section labelledBy="st-harvest" title="Harvest summary" action={<TabLink pondId={pondId} tab="harvests">Harvests tab →</TabLink>}>
      <div className="relative overflow-x-auto">
        <table className="w-full min-w-[560px] border-collapse text-left text-sm">
          <thead>
            <tr className="border-b border-gray-100">
              <th className={th}>Harvest</th>
              <th className={th}>Date</th>
              <th className={`${th} text-right`}><Term term="DOC" /></th>
              <th className={`${th} text-right`}>Biomass</th>
              <th className={`${th} text-right`}>Pieces</th>
              <th className={`${th} text-right`}><Term term="ABW" /></th>
            </tr>
          </thead>
          <tbody>
            {harvests.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-2.5 py-4 text-gray-500">
                  No harvests yet. Partials and the final fill in here, with ABW worked out from biomass ÷ pieces.
                </td>
              </tr>
            ) : (
              harvests.map((h) => {
                const kg = num(h.total_weight_kg);
                const abw = num(h.avg_weight_g) ?? (kg && h.piece_count ? (kg * 1000) / h.piece_count : null);
                return (
                  <tr key={h.id} className="border-b border-gray-50 last:border-0">
                    <td className="px-2.5 py-2 font-semibold">{h.harvest_type === 'final' ? 'Final' : 'Partial'}</td>
                    <td className={td}>{date(h.harvest_date)}</td>
                    <td className={`${td} text-right`}>{docOn(crop, h.harvest_date) ?? '—'}</td>
                    <td className={`${td} text-right`}>{kg !== null ? `${kg.toLocaleString(undefined, { maximumFractionDigits: 1 })} kg` : '—'}</td>
                    <td className={`${td} text-right`}>{h.piece_count ? h.piece_count.toLocaleString() : '—'}</td>
                    <td className={`${td} text-right`}>{abw !== null ? abwText(abw) : '—'}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {closed && crop.survival_basis === 'harvest' && survival !== null && (
        <p className="text-sm text-gray-600">
          Actual survival <span className="font-semibold tabular-nums">{survival.toFixed(1)}%</span> from harvested pieces.
        </p>
      )}
    </Section>
  );
}
